import { ChevronLeftIcon, ChevronRightIcon, ListIcon } from 'morpheus-ui';
import { useEffect, useState } from 'react';
import { IExercise } from '../../../models/exercise';
import { Button } from '../../atoms/Button';
import { Typography } from '../Typography';

interface ExercisePaginationProps {
  page: number;
  totalPages: number;
  totalExercises: number;
  currentPosition: number;
  currentExercise: IExercise;
  showContentList?: boolean;
  enableRevision?: boolean;
  isLoading?: boolean;
  inline?: boolean;
  lastPageLabel?: string;
  onLastPageClick?: () => void;
  onPrevious: () => void;
  onCheck: () => void;
  onNext: () => void;
  onEndRevision?: () => void;
  onClickContentList?: () => void;
}

export default function ExercisePagination({
  page,
  totalPages,
  currentExercise,
  totalExercises,
  currentPosition,
  showContentList = false,
  enableRevision = false,
  isLoading = false,
  inline = false,
  lastPageLabel,
  onLastPageClick,
  onPrevious,
  onCheck,
  onNext,
  onEndRevision = () => {},
  onClickContentList = () => {},
}: ExercisePaginationProps) {
  const isLastPage = page >= totalPages - 1;
  const showLastPageButton = isLastPage && lastPageLabel && onLastPageClick;
  const [innerWidth, setInnerWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setInnerWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fixedClasses = inline
    ? 'font-noto flex h-20 w-full flex-row gap-xl bg-color-lightest px-8 py-5 rounded-lg'
    : 'font-noto fixed bottom-0 left-0 flex h-20 w-full flex-row gap-xl border-t-hairline border-t-mono-color-lightest bg-color-lightest px-8 py-5 shadow-level-2';

  if (innerWidth < 992) {
    if (inline) {
      return (
        <footer className='font-noto flex w-full flex-col gap-md rounded-lg bg-color-lightest px-8 py-5'>
          <div className='flex w-full flex-row items-center gap-md'>
            <Button allowReadOnly
              variant='outlined'
              onClick={onPrevious}
              onlyIcon
              LeftIcon={ChevronLeftIcon}
              iconType='svg'
              disabled={page <= 0 || isLoading}
              classNameIcon='fill-button-color-tertiary-default'
            />
            {showContentList &&
            (currentExercise?.done ||
              !currentExercise?.hasAlternativeSelected) ? (
              <Button allowReadOnly
                LeftIcon={ListIcon}
                onClick={onClickContentList ?? (() => {})}
                variant='text'
                iconSize='large'
                className='flex-1 items-center justify-center'
                onlyIcon={innerWidth <= 390}
                iconType='svg'
                classNameIcon='fill-button-color-tertiary-default'
              >
                Lista de conteúdos
              </Button>
            ) : (
              <>
                {enableRevision ? (
                  <Button allowReadOnly
                    onClick={onEndRevision}
                    className='flex-1 items-center justify-center'
                  >
                    Finalizar revisão
                  </Button>
                ) : (
                  <Button allowReadOnly
                    onClick={onCheck}
                    disabled={
                      !(
                        currentExercise?.hasAlternativeSelected &&
                        !currentExercise?.done
                      )
                    }
                    className='flex-1 items-center justify-center'
                  >
                    Conferir
                  </Button>
                )}
              </>
            )}
            {showLastPageButton ? (
              <Button allowReadOnly
                onClick={onLastPageClick}
                className={`flex-shrink-0 ${innerWidth > 480 ? 'flex-shrink-0' : 'hidden'}`}
              >
                {lastPageLabel}
              </Button>
            ) : (
              <Button allowReadOnly
                variant='outlined'
                onlyIcon
                LeftIcon={ChevronRightIcon}
                iconType='svg'
                onClick={onNext}
                disabled={isLastPage || isLoading}
                classNameIcon='fill-button-color-tertiary-default'
              />
            )}
          </div>
          {innerWidth < 480 && (
            <Button allowReadOnly onClick={onLastPageClick} className='flex justify-center'>
              {lastPageLabel}
            </Button>
          )}
          <div className='flex items-center justify-center'>
            <Typography
              size='small'
              className='text-button-color-secondary-disabled'
            >
              Exercício {currentPosition} de {totalExercises}
            </Typography>
          </div>
        </footer>
      );
    }

    return (
      <footer className='font-noto fixed bottom-0 left-0 flex h-20 w-full flex-row gap-xl border-t-hairline border-t-mono-color-lightest bg-color-lightest px-8 py-5 shadow-level-2'>
        <Button allowReadOnly
          variant='outlined'
          onClick={onPrevious}
          onlyIcon
          LeftIcon={ChevronLeftIcon}
          iconType='svg'
          disabled={page <= 0 || isLoading}
          classNameIcon='fill-button-color-tertiary-default'
        />
        <div className='flex items-center'>
          <Typography
            size='small'
            className='text-button-color-secondary-disabled'
          >
            {currentPosition} de {totalExercises}
          </Typography>
        </div>
        {showContentList &&
        (currentExercise?.done || !currentExercise?.hasAlternativeSelected) ? (
          <Button allowReadOnly
            LeftIcon={ListIcon}
            onClick={onClickContentList ?? (() => {})}
            variant='text'
            iconSize='large'
            className='flex-1 items-center justify-center'
            onlyIcon={innerWidth <= 390}
            iconType='svg'
            classNameIcon='fill-button-color-tertiary-default'
          >
            Lista de conteúdos
          </Button>
        ) : (
          <>
            {enableRevision ? (
              <Button allowReadOnly
                onClick={onEndRevision}
                className='flex-1 items-center justify-center'
              >
                Finalizar revisão
              </Button>
            ) : (
              <Button allowReadOnly
                onClick={onCheck}
                disabled={
                  !(
                    currentExercise?.hasAlternativeSelected &&
                    !currentExercise?.done
                  )
                }
                className='flex-1 items-center justify-center'
              >
                Conferir
              </Button>
            )}
          </>
        )}
        {/* Componente repetido e invisivel para centralizar o botão da lista #GambiarraBraba*/}
        <div className='hidden items-center opacity-0 md:flex'>
          <Typography
            size='small'
            className='text-button-color-secondary-disabled'
          >
            {currentPosition} de {totalExercises}
          </Typography>
        </div>
        {showLastPageButton ? (
          <Button allowReadOnly onClick={onLastPageClick} className='flex-shrink-0'>
            {lastPageLabel}
          </Button>
        ) : (
          <Button allowReadOnly
            variant='outlined'
            onlyIcon
            LeftIcon={ChevronRightIcon}
            iconType='svg'
            onClick={onNext}
            disabled={isLastPage || isLoading}
            classNameIcon='fill-button-color-tertiary-default'
          />
        )}
      </footer>
    );
  }

  return (
    <footer className={fixedClasses}>
      <Button allowReadOnly
        variant='outlined'
        onClick={onPrevious}
        disabled={page <= 0 || isLoading}
      >
        Exercício anterior
      </Button>

      <div className='flex items-center'>
        <Typography
          size='small'
          className='text-button-color-secondary-disabled'
        >
          Exercício {currentPosition} de {totalExercises}
        </Typography>
      </div>

      {showContentList &&
      (currentExercise?.done || !currentExercise?.hasAlternativeSelected) ? (
        <Button allowReadOnly
          LeftIcon='list'
          onClick={onClickContentList}
          variant='text'
          className='flex-1 items-center justify-center'
        >
          Lista de conteúdos
        </Button>
      ) : (
        <>
          {enableRevision ? (
            <Button allowReadOnly
              onClick={onEndRevision}
              className='flex-1 items-center justify-center'
            >
              Finalizar revisão
            </Button>
          ) : (
            <Button allowReadOnly
              onClick={onCheck}
              disabled={
                !(
                  currentExercise?.hasAlternativeSelected &&
                  !currentExercise?.done
                )
              }
              className='flex-1 items-center justify-center'
            >
              Conferir
            </Button>
          )}
        </>
      )}
      {/* Componente repetido e invisivel para centralizar o botão da lista #GambiarraBraba*/}
      <div className='flex items-center opacity-0'>
        <Typography
          size='small'
          className='text-button-color-secondary-disabled'
        >
          Exercício {currentPosition} de {totalExercises}
        </Typography>
      </div>
      {showLastPageButton ? (
        <Button allowReadOnly variant='contained' onClick={onLastPageClick}>
          {lastPageLabel}
        </Button>
      ) : (
        <Button allowReadOnly
          variant='outlined'
          onClick={onNext}
          disabled={isLastPage || isLoading}
        >
          Próximo exercício
        </Button>
      )}
    </footer>
  );
}
